import { useCallback } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';

/**
 * การเดินระหว่างหน้ารายการ ↔ หน้ารายละเอียด/แก้ไข (หน้าเต็มแทนโมดัล · มติ PO 28 ต.ค. 2026)
 *
 * หน้ารายการเก็บตัวกรอง/หน้า/คำค้นไว้ใน query string → ตอนเปิดหน้ารายละเอียดแนบ URL ของรายการไปใน
 * `location.state.from` แล้วปุ่ม "กลับ" / breadcrumb / หลังบันทึก พากลับไป URL นั้น (ตัวกรองไม่หาย)
 * เปิดตรงจาก bookmark/แท็บใหม่ (ไม่มี state) = ถอยไป `fallback` (หน้ารายการเปล่า)
 */
export interface DetailNavState {
  /** URL ของหน้าที่เปิดมา (pathname + search) */
  from?: string;
}

/** state ที่ส่งไปกับ navigate/Link จากหน้ารายการ — `navigate(url, { state: detailNavState(location) })` */
export function detailNavState(location: Pick<Location, 'pathname' | 'search'>): DetailNavState {
  return { from: `${location.pathname}${location.search}` };
}

/** อ่าน `from` แบบปลอดภัย: ต้องเป็น path ภายในแอพ (ขึ้นต้น "/" ตัวเดียว) ไม่งั้นใช้ fallback */
export function readBackTarget(state: unknown, fallback: string): string {
  const from = (state as DetailNavState | null | undefined)?.from;
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) return from;
  return fallback;
}

/**
 * ใช้ในหน้ารายละเอียด/แก้ไข
 * - `backTo`: URL รายการพร้อมตัวกรองเดิม (หรือ fallback)
 * - `goBack()`: กลับไปหน้ารายการ
 * - `goTo(url)`: ไปหน้าอื่นในชุดเดียวกัน (ดู ↔ แก้ไข) โดยส่ง `from` ต่อ — กลับจากหน้าแก้ไขยังได้ตัวกรองเดิม
 */
export function useDetailNavigation(fallback: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = readBackTarget(location.state, fallback);
  const goBack = useCallback(() => navigate(backTo), [navigate, backTo]);
  const goTo = useCallback(
    (url: string, options: { replace?: boolean } = {}) =>
      navigate(url, { replace: options.replace, state: { from: backTo } satisfies DetailNavState }),
    [navigate, backTo]
  );
  return { backTo, goBack, goTo, navState: { from: backTo } as DetailNavState };
}
